import { EmptyState } from '@/components/primitives/EmptyState';
/**
 * Search (`/traffic/search`) — the active site's SEO lens, fed by the
 * Google Search Console connector (spec google-search-console).
 *
 * Surfaces the business-outcome side of perf work: daily clicks /
 * impressions, average ranking position over time, top queries (with a
 * movers signal from the window's split-half weighted positions) and
 * top pages. Data lands via the daily gsc-sync cron, so "today" is
 * always ~2 days behind — that's Google's latency, not ours.
 *
 * Three gates, in order:
 *   1. GSC not connected on this org  → connect CTA (Settings → Integrations)
 *   2. connected but no property mapped to THIS site → mapping CTA
 *   3. mapped but no rows yet (first sync pending) → patience notice
 */
import {
  DEFAULT_PAGE_RANGE,
  type PageRange,
  PageRangePill,
} from '@/components/primitives/PageRangePill';
import { SiteIsland } from '@/components/shell/SiteIsland';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useActiveSiteUuid } from '@/lib/active-site';
import {
  type SearchConsolePageRow,
  type SearchConsoleQueryRow,
  type SearchConsoleTrendRow,
  useGoogleScStatus,
  useSearchConsoleTopPages,
  useSearchConsoleTopQueries,
  useSearchConsoleTrend,
} from '@/lib/queries';
import { ArrowDown, ArrowUp, MagnifyingGlass } from '@phosphor-icons/react';
import { Link, createFileRoute } from '@tanstack/react-router';
import { type ReactElement, useState } from 'react';
import {
  Area,
  AreaChart,
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

export const Route = createFileRoute('/_app/traffic/search')({
  component: SearchPage,
});

const compactFmt = new Intl.NumberFormat('en', { notation: 'compact', maximumFractionDigits: 1 });
const pctFmt = new Intl.NumberFormat('en', { style: 'percent', maximumFractionDigits: 1 });

function SearchPage(): ReactElement {
  const siteUuid = useActiveSiteUuid();
  const [range, setRange] = useState<PageRange>(DEFAULT_PAGE_RANGE);
  const gsc = useGoogleScStatus();

  const mapped = !!gsc.data?.properties.some((p) => p.site_uuid === siteUuid && p.enabled);
  const gateReady = gsc.isSuccess;
  const dataEnabled = gateReady && !!gsc.data?.connected && mapped;

  const trend = useSearchConsoleTrend(siteUuid, { days: range, enabled: dataEnabled });
  const topQueries = useSearchConsoleTopQueries(siteUuid, { days: range, enabled: dataEnabled });
  const topPages = useSearchConsoleTopPages(siteUuid, { days: range, enabled: dataEnabled });

  return (
    <SiteIsland section="Search" parent={{ label: 'Traffic', href: '/traffic' }}>
      {siteUuid && (
        <div className="space-y-6">
          <div className="flex items-center gap-2">
            <div className="min-w-0 flex-1">
              <SearchSummary rows={trend.data} loading={!dataEnabled || trend.isLoading} />
            </div>
            <PageRangePill value={range} onChange={setRange} />
          </div>

          {gateReady && !gsc.data?.connected ? (
            <EmptyState
              icon={MagnifyingGlass}
              title="Connect Google Search Console"
              description="See search clicks, impressions and ranking positions next to your performance data — and find out whether perf work moves them."
              action={
                <Button render={<Link to="/settings/integrations" />}>Connect in Settings</Button>
              }
            />
          ) : gateReady && !mapped ? (
            <EmptyState
              icon={MagnifyingGlass}
              title="Link a Search Console property to this site"
              description="Your Google account is connected — map one of its properties to this site to start syncing search data."
              action={<Button render={<Link to="/settings/integrations" />}>Map a property</Button>}
            />
          ) : dataEnabled && trend.isSuccess && (trend.data?.length ?? 0) === 0 ? (
            <EmptyState
              icon={MagnifyingGlass}
              title="First sync on its way"
              description="The property is mapped — the first sync lands within a day and backfills the full 16 months Google keeps."
            />
          ) : (
            <>
              <SearchKpis rows={trend.data} loading={trend.isLoading} />
              <ClicksImpressionsChart rows={trend.data ?? []} loading={trend.isLoading} />
              <PositionChart rows={trend.data ?? []} loading={trend.isLoading} />
              <div className="grid gap-6 xl:grid-cols-2">
                <TopQueriesCard rows={topQueries.data ?? []} loading={topQueries.isLoading} />
                <TopPagesCard rows={topPages.data ?? []} loading={topPages.isLoading} />
              </div>
            </>
          )}
        </div>
      )}
    </SiteIsland>
  );
}

// ─── Aggregates ───────────────────────────────────────────────────────────────

interface Totals {
  clicks: number;
  impressions: number;
  ctr: number | null;
  position: number | null;
}

function totalsOf(rows: SearchConsoleTrendRow[] | undefined): Totals {
  if (!rows || rows.length === 0) return { clicks: 0, impressions: 0, ctr: null, position: null };
  let clicks = 0;
  let impressions = 0;
  let posWeight = 0;
  for (const r of rows) {
    clicks += r.clicks;
    impressions += r.impressions;
    if (r.position != null) posWeight += r.position * r.impressions;
  }
  return {
    clicks,
    impressions,
    ctr: impressions > 0 ? clicks / impressions : null,
    position: impressions > 0 ? posWeight / impressions : null,
  };
}

function SearchSummary({
  rows,
  loading,
}: {
  rows: SearchConsoleTrendRow[] | undefined;
  loading: boolean;
}): ReactElement {
  const t = totalsOf(rows);
  const clicks = loading ? '—' : compactFmt.format(t.clicks);
  const impressions = loading ? '—' : compactFmt.format(t.impressions);
  return (
    <p className="truncate text-sm text-[var(--color-mute)]">
      <span className="font-semibold tabular-nums text-[var(--color-ink)]">{clicks}</span> search
      clicks
      <span className="mx-1.5 text-[var(--color-line)]">·</span>
      <span className="font-semibold tabular-nums text-[var(--color-ink)]">{impressions}</span>{' '}
      impressions
    </p>
  );
}

function SearchKpis({
  rows,
  loading,
}: {
  rows: SearchConsoleTrendRow[] | undefined;
  loading: boolean;
}): ReactElement {
  const t = totalsOf(rows);
  const tiles: Array<{ label: string; value: string; hint: string }> = [
    { label: 'Clicks', value: compactFmt.format(t.clicks), hint: 'Search result clicks' },
    {
      label: 'Impressions',
      value: compactFmt.format(t.impressions),
      hint: 'Times shown in search',
    },
    {
      label: 'CTR',
      value: t.ctr != null ? pctFmt.format(t.ctr) : '—',
      hint: 'Clicks ÷ impressions',
    },
    {
      label: 'Avg position',
      value: t.position != null ? t.position.toFixed(1) : '—',
      hint: 'Impression-weighted; lower is better',
    },
  ];
  return (
    <div className="grid gap-px overflow-hidden rounded-xl border border-[var(--color-line)] bg-[var(--color-line)] [grid-template-columns:repeat(auto-fit,minmax(160px,1fr))]">
      {tiles.map((tile) => (
        <div key={tile.label} className="bg-[var(--color-paper)] p-4">
          <p className="text-xs font-medium text-[var(--color-mute)]" title={tile.hint}>
            {tile.label}
          </p>
          {loading ? (
            <Skeleton className="mt-1 h-7 w-16" />
          ) : (
            <p className="mt-0.5 text-xl font-semibold tabular-nums text-[var(--color-ink)]">
              {tile.value}
            </p>
          )}
        </div>
      ))}
    </div>
  );
}

// ─── Charts ───────────────────────────────────────────────────────────────────

function ChartCard({
  title,
  hint,
  children,
}: {
  title: string;
  hint?: string;
  children: ReactElement;
}): ReactElement {
  return (
    <section className="rounded-xl border border-[var(--color-line)] bg-[var(--color-paper)]">
      <header className="flex items-center justify-between gap-2 border-b border-[var(--color-line-soft)] px-4 py-3">
        <h3 className="text-sm font-semibold text-[var(--color-ink)]">{title}</h3>
        {hint && <span className="text-xs text-[var(--color-mute)]">{hint}</span>}
      </header>
      <div className="px-2 py-3">{children}</div>
    </section>
  );
}

const dayFmt = new Intl.DateTimeFormat('en', { month: 'short', day: 'numeric' });

function fmtDay(day: string): string {
  const d = new Date(`${day}T00:00:00Z`);
  return Number.isNaN(d.getTime()) ? day : dayFmt.format(d);
}

function ClicksImpressionsChart({
  rows,
  loading,
}: {
  rows: SearchConsoleTrendRow[];
  loading: boolean;
}): ReactElement {
  return (
    <ChartCard title="Clicks & impressions" hint="Google restates the most recent ~2 days">
      {loading ? (
        <Skeleton className="h-56 w-full" />
      ) : (
        <ResponsiveContainer width="100%" height={224}>
          <AreaChart data={rows} margin={{ top: 4, right: 8, bottom: 0, left: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--color-line-soft)" vertical={false} />
            <XAxis
              dataKey="day"
              tickFormatter={fmtDay}
              tick={{ fontSize: 11, fill: 'var(--color-mute)' }}
              tickLine={false}
              axisLine={false}
              minTickGap={32}
            />
            <YAxis
              yAxisId="clicks"
              tick={{ fontSize: 11, fill: 'var(--color-mute)' }}
              tickFormatter={(v: number) => compactFmt.format(v)}
              tickLine={false}
              axisLine={false}
              width={40}
            />
            <YAxis
              yAxisId="impressions"
              orientation="right"
              tick={{ fontSize: 11, fill: 'var(--color-mute)' }}
              tickFormatter={(v: number) => compactFmt.format(v)}
              tickLine={false}
              axisLine={false}
              width={44}
            />
            <Tooltip
              labelFormatter={(l) => fmtDay(String(l))}
              formatter={(value, name) => [
                compactFmt.format(Number(value)),
                name === 'clicks' ? 'Clicks' : 'Impressions',
              ]}
            />
            <Area
              yAxisId="impressions"
              dataKey="impressions"
              type="monotone"
              stroke="var(--color-mute)"
              strokeWidth={1}
              fill="var(--color-mute)"
              fillOpacity={0.08}
            />
            <Area
              yAxisId="clicks"
              dataKey="clicks"
              type="monotone"
              stroke="var(--color-accent-brand)"
              strokeWidth={1.5}
              fill="var(--color-accent-brand)"
              fillOpacity={0.12}
            />
          </AreaChart>
        </ResponsiveContainer>
      )}
    </ChartCard>
  );
}

function PositionChart({
  rows,
  loading,
}: {
  rows: SearchConsoleTrendRow[];
  loading: boolean;
}): ReactElement {
  return (
    <ChartCard title="Average position" hint="Lower is better — the axis is flipped to match">
      {loading ? (
        <Skeleton className="h-40 w-full" />
      ) : (
        <ResponsiveContainer width="100%" height={160}>
          <LineChart data={rows} margin={{ top: 4, right: 8, bottom: 0, left: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--color-line-soft)" vertical={false} />
            <XAxis
              dataKey="day"
              tickFormatter={fmtDay}
              tick={{ fontSize: 11, fill: 'var(--color-mute)' }}
              tickLine={false}
              axisLine={false}
              minTickGap={32}
            />
            <YAxis
              reversed
              domain={['auto', 'auto']}
              tick={{ fontSize: 11, fill: 'var(--color-mute)' }}
              tickFormatter={(v: number) => v.toFixed(0)}
              tickLine={false}
              axisLine={false}
              width={40}
            />
            <Tooltip
              labelFormatter={(l) => fmtDay(String(l))}
              formatter={(value) => [Number(value).toFixed(1), 'Avg position']}
            />
            <Line
              dataKey="position"
              type="monotone"
              stroke="var(--color-accent-brand)"
              strokeWidth={1.5}
              dot={false}
              connectNulls
            />
          </LineChart>
        </ResponsiveContainer>
      )}
    </ChartCard>
  );
}

// ─── Tables ───────────────────────────────────────────────────────────────────

/** Ranking delta badge: prev → recent weighted position. Negative delta =
 * climbing (positions count down toward #1). Suppressed under ±0.5 —
 * sub-half-position noise reads as churn, not signal. */
function Mover({ row }: { row: SearchConsoleQueryRow }): ReactElement | null {
  if (row.position_recent == null || row.position_prev == null) return null;
  const delta = row.position_recent - row.position_prev;
  if (Math.abs(delta) < 0.5) return null;
  const climbing = delta < 0;
  return (
    <span
      className={`inline-flex items-center gap-0.5 text-xs font-medium ${
        climbing ? 'text-[var(--color-success)]' : 'text-[var(--color-danger)]'
      }`}
      title={`${row.position_prev.toFixed(1)} → ${row.position_recent.toFixed(1)} over the window halves`}
    >
      {climbing ? (
        <ArrowUp size={12} aria-hidden="true" />
      ) : (
        <ArrowDown size={12} aria-hidden="true" />
      )}
      {Math.abs(delta).toFixed(1)}
    </span>
  );
}

function TopQueriesCard({
  rows,
  loading,
}: {
  rows: SearchConsoleQueryRow[];
  loading: boolean;
}): ReactElement {
  return (
    <section className="rounded-xl border border-[var(--color-line)] bg-[var(--color-paper)]">
      <header className="border-b border-[var(--color-line-soft)] px-4 py-3">
        <h3 className="text-sm font-semibold text-[var(--color-ink)]">Top queries</h3>
      </header>
      {loading ? (
        <div className="space-y-2 p-4">
          <Skeleton className="h-5 w-full" />
          <Skeleton className="h-5 w-4/5" />
          <Skeleton className="h-5 w-3/5" />
        </div>
      ) : rows.length === 0 ? (
        <p className="p-4 text-sm text-[var(--color-mute)]">No query data in this window.</p>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Query</TableHead>
              <TableHead className="text-right">Clicks</TableHead>
              <TableHead className="text-right">Impr.</TableHead>
              <TableHead className="text-right">CTR</TableHead>
              <TableHead className="text-right">Pos.</TableHead>
              <TableHead className="text-right">Trend</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.slice(0, 20).map((r) => (
              <TableRow key={r.query}>
                <TableCell className="max-w-56 truncate font-medium" title={r.query}>
                  {r.query}
                </TableCell>
                <TableCell className="text-right tabular-nums">
                  {compactFmt.format(r.clicks)}
                </TableCell>
                <TableCell className="text-right tabular-nums">
                  {compactFmt.format(r.impressions)}
                </TableCell>
                <TableCell className="text-right tabular-nums">
                  {r.ctr != null ? pctFmt.format(r.ctr) : '—'}
                </TableCell>
                <TableCell className="text-right tabular-nums">
                  {r.position != null ? r.position.toFixed(1) : '—'}
                </TableCell>
                <TableCell className="text-right">
                  <Mover row={r} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </section>
  );
}

function TopPagesCard({
  rows,
  loading,
}: {
  rows: SearchConsolePageRow[];
  loading: boolean;
}): ReactElement {
  return (
    <section className="rounded-xl border border-[var(--color-line)] bg-[var(--color-paper)]">
      <header className="border-b border-[var(--color-line-soft)] px-4 py-3">
        <h3 className="text-sm font-semibold text-[var(--color-ink)]">Top pages</h3>
      </header>
      {loading ? (
        <div className="space-y-2 p-4">
          <Skeleton className="h-5 w-full" />
          <Skeleton className="h-5 w-4/5" />
          <Skeleton className="h-5 w-3/5" />
        </div>
      ) : rows.length === 0 ? (
        <p className="p-4 text-sm text-[var(--color-mute)]">No page data in this window.</p>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Page</TableHead>
              <TableHead className="text-right">Clicks</TableHead>
              <TableHead className="text-right">Impr.</TableHead>
              <TableHead className="text-right">CTR</TableHead>
              <TableHead className="text-right">Pos.</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.slice(0, 20).map((r) => (
              <TableRow key={r.page_url}>
                <TableCell className="max-w-64 truncate font-medium" title={r.page_url}>
                  {pathOf(r.page_url)}
                </TableCell>
                <TableCell className="text-right tabular-nums">
                  {compactFmt.format(r.clicks)}
                </TableCell>
                <TableCell className="text-right tabular-nums">
                  {compactFmt.format(r.impressions)}
                </TableCell>
                <TableCell className="text-right tabular-nums">
                  {r.ctr != null ? pctFmt.format(r.ctr) : '—'}
                </TableCell>
                <TableCell className="text-right tabular-nums">
                  {r.position != null ? r.position.toFixed(1) : '—'}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </section>
  );
}

function pathOf(url: string): string {
  try {
    const u = new URL(url);
    return u.pathname + u.search;
  } catch {
    return url;
  }
}
